// Angular
import { Injectable } from '@angular/core';

// Afidium
import {
    IGetBookingsAirResponse,
    IGetBookingsStayResponse,
    IPrice,
    ITraveller,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import {
    ICellValue,
    ILinesValues,
    IPriceLine,
    IPriceValue,
    ITablePrice,
    ITaxConfig,
    ITotalColumn,
    PriceColumnType,
    TPriceLine,
    TPriceValues,
} from "./ITablePrice";
import { TravellersMapperService } from './mappers/travellers-mapper.service';
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { PriceMapperService } from './mappers/price-mapper.service';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BookingService } from 'libraries/service/booking/booking.service';
import { Yii } from 'libraries/interfaces/Yii';
import { TOTAL_KEY } from './mappers/keys-mapper.service';
import { ApiPricingXState, ApiPricingXType } from 'libraries/service/resources/booking/interfaces/ApiSearchParameters';
import { IQuote } from 'libraries/service/resources/airTrip/IGetAirTripQuotes';
import { ITravellerObject } from 'libraries/service/resources/hotel/ISearchParameters';
import { TravellerXType } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { EXRateType } from "libraries/interfaces/ITotal";

@Injectable()
export class BookingPriceCardService {
    public constructor(
        private readonly travellersMapperService: TravellersMapperService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly priceMapperService: PriceMapperService,
        private readonly bookingService: BookingService,
        private readonly permissionResourceService: PermissionResourceService,
    ) {
    }

    public constructStayPriceData(booking: IGetBookingsStayResponse): Observable<ITablePrice> {
        return this.configurationResourceService
            .get<ITaxConfig[]>(['wui', 'shared_air', 'config', 'tax', 'tax-config.json'], [])
            .pipe(
                map((config: ITaxConfig[]): ITablePrice => {
                    return {
                        lines: this.priceMapperService.extractSectorsLines(
                            booking.price,
                            booking.sector,
                            booking.property,
                        )
                    };
                })
            );
    }

    public constructPriceData(booking: TGetBookingResponse): Observable<ITablePrice> {
        return this.configurationResourceService
            .get<ITaxConfig[]>(['wui', 'shared_air', 'config', 'tax', 'tax-config.json'], [])
            .pipe(
                map((config: ITaxConfig[]): ITablePrice => {
                    const allTravellersIds: string[] = (booking.traveller || []).map((traveller: ITraveller): string => traveller.xId);

                    const cells: ICellValue[] = this.priceMapperService.extractSectorsTotal(
                        booking.price,
                        booking.fareComponent,
                        allTravellersIds
                    );

                    const sectorsLines: TPriceLine[] = this.priceMapperService.extractSectorsLines(
                        booking.price,
                        (booking as IGetBookingsStayResponse | IGetBookingsAirResponse).sector,
                        (booking as IGetBookingsStayResponse).property
                    );

                    const details: ILinesValues = this.priceMapperService.extractDetails(
                        booking.price,
                        Yii.t("wui-shared-js", "Prix de base"),
                        allTravellersIds,
                        config
                    );

                    const cost: ILinesValues = this.priceMapperService.extractTotal(
                        EXRateType.cost,
                        Yii.t("wui-shared-js", "Total achat"),
                        booking.price,
                        allTravellersIds,
                        this.bookingService.isAirBooking(booking) ? ApiPricingXState.Manual : ApiPricingXState.Guaranteed,
                    );

                    const retail: ILinesValues = this.priceMapperService.extractTotal(
                        EXRateType.retail,
                        Yii.t("wui-shared-js", "Total vente"),
                        booking.price,
                        allTravellersIds,
                        this.bookingService.isAirBooking(booking) ? ApiPricingXState.Manual : ApiPricingXState.Guaranteed,
                    );

                    const values: TPriceValues = new Map();

                    addCells(cells, values);
                    addCells(details.values, values);
                    addCells(cost.values, values);
                    addCells(retail.values, values);

                    const lines: TPriceLine[] = [];

                    for (const sectorLine of sectorsLines) {
                        const childLines: TPriceLine[] = details.lines.filter((line: IPriceLine): boolean => line.parentKey === sectorLine.key);

                        lines.push({
                            ...sectorLine,
                            hasChildren: Boolean(childLines.length)
                        });
                        lines.push(...childLines);
                    }

                    lines.push(...cost.lines);
                    lines.push(...retail.lines);

                    const totalColumn: ITotalColumn = {
                        key: TOTAL_KEY,
                        type: PriceColumnType.total,
                        text: Yii.t("wui-shared-js", "Total"),
                    };

                    return {
                        lines,
                        columns: [
                            totalColumn,
                            ...this.travellersMapperService.getTravellerColumns(booking.traveller)
                        ],
                        values,
                    };
                })
            );
    }


    /**
     * WARN ! This is a method thunk as temporary while the data schema isn't harmonized yet between IQBair quotes and SASHA bookings
     */
    public constructPriceDataForIQBAQuote(booking: IQuote, priceXType?: EXRateType, updatedPrices?: IPrice[]): Observable<ITablePrice> {
        return forkJoin(
            [
                this.configurationResourceService
                    .get<ITaxConfig[]>(['wui', 'shared_air', 'config', 'tax', 'tax-config.json'], []),
                this.permissionResourceService.getPermissions(),
            ]
        )
            .pipe(
                map(([config, permissions]: [ITaxConfig[], Set<string>]): ITablePrice => {
                    const allTravellers: ITraveller[] = (Object.entries(booking.traveller) || [])
                        .map(([key, {
                            type,
                            identityCard,
                            passport,
                            ...traveller
                        }]: [string, ITravellerObject]): ITraveller => {
                            // @ts-ignore
                            return {
                                ...traveller,
                                xId: key,
                                xType: TravellerXType[type.toLowerCase()]
                            };
                        });
                    const allTravellersIds: string[] = (allTravellers || []).map((traveller: ITraveller): string => traveller.xId);
                    const cells: ICellValue[] = this.priceMapperService.extractSectorsTotal(
                        updatedPrices || booking.price,
                        undefined,
                        allTravellersIds
                    );

                    const values: TPriceValues = new Map();
                    addCells(cells, values);
                    const lines: TPriceLine[] = [];

                    if (permissions.has("iqba.booking.price.cost.show") && (!priceXType || priceXType !== EXRateType.retail)) {
                        const costDetails: ILinesValues = this.priceMapperService.extractDetails(
                            updatedPrices || booking.price,
                            Yii.t("wui-shared-js", "Prix de base"),
                            allTravellersIds,
                            config
                        );

                        const cost: ILinesValues = this.priceMapperService.extractTotal(
                            EXRateType.cost,
                            Yii.t("wui-shared-js", "Total achat"),
                            updatedPrices || booking.price,
                            allTravellersIds,
                            ApiPricingXState.Guaranteed,
                        );
                        addCells(costDetails.values, values);
                        addCells(cost.values, values);
                        lines.push(...costDetails.lines);
                        lines.push(...cost.lines);
                    }

                    if (permissions.has("iqba.booking.price.retail.show") && (!priceXType || priceXType !== EXRateType.cost)) {
                        const retail: ILinesValues = this.priceMapperService.extractTotal(
                            EXRateType.retail,
                            Yii.t("wui-shared-js", "Total vente"),
                            booking.price,
                            allTravellersIds,
                            ApiPricingXState.Guaranteed,
                        );

                        const retailDetails: ILinesValues = this.priceMapperService.extractDetails(
                            booking.price,
                            Yii.t("wui-shared-js", "Prix de base"),
                            allTravellersIds,
                            config,
                            EXRateType.retail
                        );
                        addCells(retailDetails.values, values);
                        lines.push(...retailDetails.lines);
                        addCells(retail.values, values);
                        lines.push(...retail.lines);
                    }

                    const totalColumn: ITotalColumn = {
                        key: TOTAL_KEY,
                        type: PriceColumnType.total,
                        text: Yii.t("wui-shared-js", "Total"),
                    };
                    return {
                        lines,
                        columns: [
                            totalColumn,
                            ...this.travellersMapperService.getTravellerColumns(allTravellers)
                        ],
                        values,
                    };
                })
            );
    }
}

function addCells(cells: ICellValue[], priceValues: TPriceValues): void {
    for (const cell of cells) {
        let line: Map<string, IPriceValue> = priceValues.get(cell.lineKey);

        if (!line) {
            line = new Map();
            priceValues.set(cell.lineKey, line);
        }

        line.set(cell.columnKey, cell.value);
    }
}
